import { useId, useRef, useState } from 'react';
import { RetryableError } from '../../../shared/RetryableError';
import { ApiError } from '../../identity/types';
import { formatReportDate } from '../../reports/formatting';
import { jobRoleGateway } from '../index';
import { PAY_BAND_CONFLICT_MESSAGE, isConcurrencyConflict, isForbidden, isPayBandReviewRequired, jobRoleErrorMessage } from '../jobRoleErrors';
import {
  basisUnit, convertAmount, emptyPayBandForm, formFromBand, formatBandRange, formatHours, formatMoney, inputFromPayBandForm, roundMoney,
  type PayBandForm as PayBandFormValues, type PayBandIssue,
} from '../payBand';
import { useJobRoleCapabilities } from '../useCapabilities';
import { COMPENSATION_DISCLAIMER, JobRoleStatus, type JobRoleDetail, type PayBand } from '../types';
import { BenchmarkPanel } from './BenchmarkPanel';
import { ConfirmDialog } from './ConfirmDialog';
import { PayBandForm } from './PayBandForm';
import { useTabLoad } from './tabLoad';

/** `yyyy-MM-dd` as a date; the band's dates are calendar days, not instants. */
const day = (value: string | null): string => (value ? formatReportDate(value) : '—');

function BandFacts({ band }: { band: PayBand }) {
  const other = band.basis === 'Hourly' ? 'Annual' : 'Hourly';
  const equivalent = (value: number) => formatMoney(roundMoney(convertAmount(value, band.basis, other, band.annualHoursAssumption)), other);
  return (
    <>
      <p className="jr-band-range" data-testid="pay-band-range">{formatBandRange(band)}</p>
      <dl className="jr-facts jr-facts--tight">
        <div className="jr-facts__row"><dt>Pay basis</dt><dd>{band.basis === 'Hourly' ? 'Hourly' : 'Annual'}</dd></div>
        <div className="jr-facts__row"><dt>Minimum</dt><dd>{formatMoney(band.minimum, band.basis)} {basisUnit(band.basis)}</dd></div>
        <div className="jr-facts__row"><dt>Midpoint</dt><dd>{formatMoney(band.midpoint, band.basis)} {basisUnit(band.basis)}</dd></div>
        <div className="jr-facts__row"><dt>Maximum</dt><dd>{formatMoney(band.maximum, band.basis)} {basisUnit(band.basis)}</dd></div>
        <div className="jr-facts__row"><dt>Annual hours assumption</dt><dd>{formatHours(band.annualHoursAssumption)}</dd></div>
        <div className="jr-facts__row"><dt>In effect since</dt><dd>{day(band.effectiveFromDate)}</dd></div>
        {band.note && <div className="jr-facts__row jr-facts__row--wide"><dt>Note</dt><dd>{band.note}</dd></div>}
      </dl>
      <p className="jr-hint">
        Equivalent {other.toLowerCase()} range at {formatHours(band.annualHoursAssumption)} annual hours:{' '}
        {equivalent(band.minimum)} {'–'} {equivalent(band.maximum)} {basisUnit(other)}. The conversion is only a multiplication or division by those hours.
      </p>
    </>
  );
}

function PayBandHistory({ roleId, nonce }: { roleId: string; nonce: number }) {
  const { state, retry } = useTabLoad<PayBand[]>((signal) => jobRoleGateway.listPayBandHistory(roleId, signal), [roleId, nonce], 'Could not load the pay band history.');
  if (state.kind === 'loading') return <p className="muted" role="status">Loading pay band history{'…'}</p>;
  if (state.kind === 'forbidden') return <p className="jr-notice" role="note">You do not have permission to view the pay band history.</p>;
  if (state.kind === 'error') return <RetryableError message={state.message} onRetry={retry} />;
  if (state.data.length === 0) return <p className="muted">This role has never had a pay band.</p>;
  return (
    <div className="jr-table-scroll" role="region" aria-label="Pay band history table" tabIndex={0}>
      <table className="jr-table" data-testid="pay-band-history">
        <caption className="jr-sr-only">Employer pay bands of this role, newest first. Earlier bands are kept, never changed.</caption>
        <thead>
          <tr>
            <th scope="col">From</th><th scope="col">To</th><th scope="col">Basis</th><th scope="col">Minimum</th><th scope="col">Midpoint</th>
            <th scope="col">Maximum</th><th scope="col">Annual hours</th><th scope="col">Note</th>
          </tr>
        </thead>
        <tbody>
          {state.data.map((b) => (
            <tr key={b.id}>
              <th scope="row">{day(b.effectiveFromDate)}</th>
              <td>{b.effectiveToDate ? day(b.effectiveToDate) : 'Current'}</td>
              <td>{b.basis}</td>
              <td>{formatMoney(b.minimum, b.basis)}</td>
              <td>{formatMoney(b.midpoint, b.basis)}</td>
              <td>{formatMoney(b.maximum, b.basis)}</td>
              <td>{formatHours(b.annualHoursAssumption)}</td>
              <td>{b.note ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The employer pay band of one role: the company's own policy, never an employee's pay and never a benchmark in disguise. Anyone who
 * can read the role sees it. Setting, changing or ending it needs its own permission, which this screen cannot know in advance, so it
 * offers the actions and treats the first 403 as "you can view but not change" for the rest of the visit. A change appends a new band
 * and keeps the old one in the history.
 */
function PayBandCard({ role }: { role: JobRoleDetail }) {
  const uid = useId();
  const [version, setVersion] = useState(0);
  const { state, retry } = useTabLoad<PayBand | null>((signal) => jobRoleGateway.getPayBand(role.id, signal), [role.id, version], 'Could not load the employer pay band.');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  // Whether to OFFER Set / Edit / End: the server's capability flag (guided fields work: fixes the dead end where everyone was offered a button the
  // server then refused), AND no 403 seen this visit (a flag is only a hint; a refusal still turns the card read-only).
  const { caps } = useJobRoleCapabilities();
  const [notRefused, setCanManage] = useState(true);
  const canManage = notRefused && (caps ? caps.canManagePayBand : true);
  const [busy, setBusy] = useState(false);
  const [serverIssues, setServerIssues] = useState<PayBandIssue[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyNonce, setHistoryNonce] = useState(0);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  const reload = () => { setVersion((n) => n + 1); setHistoryNonce((n) => n + 1); };
  const archived = role.status === JobRoleStatus.Archived;

  async function save(values: PayBandFormValues, band: PayBand | null) {
    setBusy(true);
    setServerIssues([]);
    setFormError(null);
    try {
      await jobRoleGateway.setPayBand(role.id, inputFromPayBandForm(values, band?.concurrencyVersion));
      setMode('view');
      setMessage({ kind: 'ok', text: band ? 'The pay band was changed. The earlier band is kept in the history.' : 'The pay band was set.' });
      reload();
    } catch (err) {
      if (isForbidden(err)) {
        setCanManage(false);
        setMode('view');
        setMessage(null);
      } else if (isConcurrencyConflict(err)) {
        // Someone else changed the band: show the latest one and let the person decide again.
        setMode('view');
        setMessage({ kind: 'error', text: PAY_BAND_CONFLICT_MESSAGE });
        reload();
      } else if (isPayBandReviewRequired(err)) {
        setServerIssues([{ key: 'reviewed', message: 'Confirm that you have reviewed this pay band before saving it.' }]);
      } else {
        setFormError(jobRoleErrorMessage(err, 'Could not save the pay band.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function end(band: PayBand) {
    setBusy(true);
    setEndError(null);
    try {
      await jobRoleGateway.endPayBand(role.id, band.concurrencyVersion);
      setConfirmEnd(false);
      setMessage({ kind: 'ok', text: 'The pay band was ended. It is kept in the history.' });
      reload();
    } catch (err) {
      if (isForbidden(err)) {
        setCanManage(false);
        setConfirmEnd(false);
        setMessage(null);
      } else if (err instanceof ApiError && err.status === 404) {
        // PAY_BAND_NOT_FOUND: someone already ended it. Show the latest state instead of an error inside the dialog.
        setConfirmEnd(false);
        setMessage({ kind: 'error', text: 'This role no longer has a current pay band, so there is nothing to end.' });
        reload();
      } else {
        if (isConcurrencyConflict(err)) reload();
        setEndError(isConcurrencyConflict(err) ? PAY_BAND_CONFLICT_MESSAGE : jobRoleErrorMessage(err, 'Could not end the pay band.'));
      }
    } finally {
      setBusy(false);
    }
  }

  const band = state.kind === 'ready' ? state.data : null;

  return (
    <section className="jr-card" aria-labelledby={`${uid}-title`} data-testid="pay-band-card">
      <h4 id={`${uid}-title`} className="jr-card__title">Employer pay band</h4>
      <p className="muted">
        Your company&apos;s own pay range for this role, kept separate from market benchmarks. It is a guide, not an employee&apos;s pay: adding it
        never changes anyone&apos;s rate.
      </p>

      {message && <p className={message.kind === 'ok' ? 'jr-notice jr-notice--ok' : 'jr-error jr-error--block'} role={message.kind === 'ok' ? 'status' : 'alert'}>{message.kind === 'ok' && <span aria-hidden="true">{'✓'} </span>}{message.text}</p>}

      {state.kind === 'loading' && <p className="muted" role="status">Loading the employer pay band{'…'}</p>}
      {state.kind === 'forbidden' && <p className="jr-notice" role="note">You do not have permission to view this role&apos;s pay band.</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={retry} />}

      {state.kind === 'ready' && mode === 'view' && (
        <>
          {band ? <BandFacts band={band} /> : <p data-testid="pay-band-none"><strong>Not set.</strong> <span className="muted">This role has no employer pay band.</span></p>}
          {!canManage && (
            <p className="jr-readonly" role="note" data-testid="pay-band-readonly">
              <span aria-hidden="true">{'🔒'} </span>
              You can view the pay band but not change it. Setting, changing or ending a pay band needs a separate permission. Ask a company administrator if you need it.
            </p>
          )}
          {canManage && archived && <p className="jr-notice" role="note">This role is archived. It keeps its pay band, but a new band cannot be set until the role is restored.</p>}
          <div className="jr-actions">
            {canManage && !archived && (
              <button ref={editButtonRef} type="button" className="jr-btn jr-btn--primary" onClick={() => { setMessage(null); setServerIssues([]); setFormError(null); setMode('edit'); }}>
                {band ? 'Edit pay band' : 'Set pay band'}
              </button>
            )}
            {canManage && band && (
              <button type="button" className="jr-btn jr-btn--secondary" onClick={() => { setEndError(null); setConfirmEnd(true); }}>End pay band</button>
            )}
            <button type="button" className="jr-btn jr-btn--secondary" aria-expanded={historyOpen} onClick={() => setHistoryOpen((v) => !v)}>
              {historyOpen ? 'Hide pay band history' : 'Show pay band history'}
            </button>
          </div>
          {canManage && <p className="jr-hint">Setting or changing a pay band needs its own permission, separate from viewing roles.</p>}
        </>
      )}

      {state.kind === 'ready' && mode === 'edit' && (
        <PayBandForm
          key={band?.id ?? 'new'}
          initial={band ? formFromBand(band) : emptyPayBandForm()}
          title={band ? 'Change the employer pay band' : 'Set the employer pay band'}
          submitLabel={band ? 'Save new pay band' : 'Save pay band'}
          busy={busy}
          serverIssues={serverIssues}
          onSubmit={(values) => void save(values, band)}
          onCancel={() => { setMode('view'); setServerIssues([]); setFormError(null); window.setTimeout(() => editButtonRef.current?.focus(), 0); }}
        >
          {band && <p className="jr-hint">Saving adds a new band from today and ends the current one. The current band stays in the history.</p>}
          {formError && <p className="jr-error jr-error--block" role="alert">{formError}</p>}
        </PayBandForm>
      )}

      {historyOpen && state.kind === 'ready' && (
        <div className="jr-history">
          <h5>Pay band history</h5>
          <PayBandHistory roleId={role.id} nonce={historyNonce} />
        </div>
      )}

      {confirmEnd && band && (
        <ConfirmDialog title="End this pay band?" confirmLabel="End pay band" busy={busy} error={endError} onCancel={() => setConfirmEnd(false)} onConfirm={() => void end(band)}>
          <p>This role will have no current employer pay band.</p>
          <ul className="jr-plain-list">
            <li>The band is end-dated, not deleted. It stays in the history.</li>
            <li>Ending a band never changes anyone&apos;s pay.</li>
          </ul>
        </ConfirmDialog>
      )}
    </section>
  );
}

/**
 * Role detail, Compensation tab (PR 2): a disclaimer, the employer pay band, and the market benchmark. The three kinds of value stay
 * apart: the benchmark is reference data, the pay band is the employer's policy, and an employee's actual pay is never shown on this page.
 */
export function CompensationTab({ role }: { role: JobRoleDetail }) {
  const uid = useId();
  const { caps, loaded } = useJobRoleCapabilities();
  return (
    <div className="jr-compensation" data-testid="compensation-tab">
      <p className="jr-notice" role="note" data-testid="compensation-disclaimer">{COMPENSATION_DISCLAIMER}</p>
      <PayBandCard role={role} />
      <section className="jr-card" aria-labelledby={`${uid}-bench`} data-testid="benchmark-card">
        <h4 id={`${uid}-bench`} className="jr-card__title">Market benchmark</h4>
        <p className="muted">Reference data about what a source reports for this occupation (by SOC code). It does not set or suggest a pay rate.</p>
        {/* Not asked before the capability answer is in: a caller who may not view benchmarks never triggers the request. */}
        {loaded ? <BenchmarkPanel roleId={role.id} allowed={caps ? caps.canViewBenchmarks : undefined} /> : <p className="muted" role="status">Loading market benchmarks{'…'}</p>}
      </section>
      <p className="muted jr-hint">No employee&apos;s actual pay is shown on this page, and assigning a role to an employee never changes their pay.</p>
    </div>
  );
}
